import { createRouter, createWebHistory, type NavigationGuardNext, type RouteLocationNormalized } from 'vue-router';
import {
  ROUTE_NAME_ACCOUNT_RECOVERY_EMAIL_CONFIRMATION,
  ROUTE_NAME_ACCOUNT_SET_PASSWORD,
  ROUTE_NAME_BOOKING_FORM_DATED,
  ROUTE_NAME_BOOKING_FORM_TEMPLATE,
  ROUTE_NAME_BOOKING_VIEW,
  ROUTE_NAME_CONTACT_US_VIEW,
  ROUTE_NAME_CREATE_ACCESS,
  ROUTE_NAME_CUSTOMER_CHANGE_PASSWORD,
  ROUTE_NAME_CUSTOMER_FAVOURITE_ADDRESSES,
  ROUTE_NAME_CUSTOMER_HOME_VIEW,
  ROUTE_NAME_CUSTOMER_MODIFY_ASSISTIVE_DEVICES,
  ROUTE_NAME_CUSTOMER_MODIFY_INFO,
  ROUTE_NAME_CUSTOMER_PROFILE,
  ROUTE_NAME_CUSTOMER_TRIP_DETAILS_DATED,
  ROUTE_NAME_CUSTOMER_TRIP_DETAILS_TEMPLATE,
  ROUTE_NAME_CUSTOMER_TRIPS,
  ROUTE_NAME_CUSTOMER_TRIPS_DATED,
  ROUTE_NAME_CUSTOMER_TRIPS_LIST,
  ROUTE_NAME_CUSTOMER_TRIPS_TEMPLATE,
  ROUTE_NAME_CUSTOMER_VEHICLE_TRACKING,
  ROUTE_NAME_CUSTOMER_VIEW,
  ROUTE_NAME_FIRST_CONNECTION,
  ROUTE_NAME_HISTORY_FORM_EMAIL,
  ROUTE_NAME_HISTORY_TRIP_DETAILS,
  ROUTE_NAME_HISTORY_VIEW,
  ROUTE_NAME_NOT_FOUND,
  ROUTE_NAME_PASSWORD_LOST,
  ROUTE_NAME_SIGN_IN,
  ROUTE_NAME_SIGNED_OUT_CONTACT_US,
  ROUTE_PATH_ACCOUNT_BASE,
  ROUTE_PATH_CLIENT_BASE,
} from '@/config/routeNames';
import BookingView from '@/views/customer/BookingView.vue';
import CustomerTripsDatedView from '@/views/customer/CustomerTripsDatedView.vue';
import CustomerTripsTemplateView from '@/views/customer/CustomerTripsTemplateView.vue';
import CustomerTripsView from '@/views/customer/CustomerTripsView.vue';
import HomeView from '@/views/customer/HomeView.vue';
import ProfileView from '@/views/customer/ProfileView.vue';
import ChangePasswordView from '@/views/customer/ChangePasswordView.vue';
import HistoryView from '@/views/customer/HistoryView.vue';
import HistoryFormView from '@/views/customer/HistoryFormView.vue';
import ModifyAssistiveDevicesView from '@/views/customer/ModifyAssistiveDevicesView.vue';
import ModifyCustomerInfoView from '@/views/customer/ModifyCustomerInfoView.vue';
import ContactUsView from '@/views/ContactUsView.vue';

import {
  URI_ACCOUNT_CONFIRMATION_EMAIL_QUERY_PARAM_NAME,
  URI_ACCOUNT_CONFIRMATION_FILE_NUMBER_QUERY_PARAM_NAME,
  URI_ACCOUNT_CONFIRMATION_TYPE_QUERY_PARAM_NAME,
} from '@/config';
import serviceRequestModificationStore from '@/store/serviceRequestModification.store.ts';
import { storeToRefs } from 'pinia';
import FavouriteAddressesView from '@/views/customer/FavouriteAddressesView.vue';
import { areCookiesEnabled, notifyCookiesDisabled } from '@/composables/useCookieValidation';
import useCustomerStore from '@/store/customer.store';
import useCustomerProvider from '@/components/providers/CustomerProvider/useCustomerProvider';

/** flag indiquant si le panneau de notification pour les cookies désactivé devrait s'afficher ou pas */
let cookiesDisabledNotificationPending = false;

/**
 * Vérifie que les cookies soient activés pour les routes qui sont spécifiques à la section client. passée en
 * paramètres. Dans el cas contraire, un message d'avertissement est affiché et l'utilisateur est redirigé sur la page
 * de connexion de l'application.
 */
function validateClientCookiesEnabled(to: RouteLocationNormalized) {
  const isClientRoute = to.matched.some((record) => record.path === `/${ROUTE_PATH_CLIENT_BASE}`);

  if (!isClientRoute || areCookiesEnabled()) {
    return true;
  }

  cookiesDisabledNotificationPending = true;

  return {
    name: ROUTE_NAME_SIGN_IN,
    replace: true,
  };
}

/**
 * Empêche l'accès à la gestion des favoris lorsque l'utilisateur est restreint à une liste d'adresses et qu'il tente
 * d'accéder directement à la page de favoris ou de la recharger.
 */
async function blockFavouriteAddressesWhenRestricted(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
  const customerStore = useCustomerStore();
  let { addressesRestricted } = customerStore;

  if (!customerStore.customer.fileNumber) {
    const { data } = await useCustomerProvider().refresh();
    addressesRestricted = data?.areAddressesRestricted ?? false;
  }

  if (addressesRestricted) {
    next({ name: ROUTE_NAME_CUSTOMER_PROFILE, replace: true });
  } else {
    next();
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior() {
    return { top: 0 };
  },
  routes: [
    {
      path: `/${ROUTE_PATH_CLIENT_BASE}`,
      alias: '/',
      name: ROUTE_NAME_CUSTOMER_VIEW,
      component: () => import('@/views/customer/CustomerBaseView.vue'),
      redirect: `/${ROUTE_PATH_CLIENT_BASE}/accueil`,
      children: [
        {
          name: ROUTE_NAME_CUSTOMER_HOME_VIEW,
          path: 'accueil',
          component: HomeView,
        },
        {
          name: ROUTE_NAME_CONTACT_US_VIEW,
          path: `/${ROUTE_PATH_CLIENT_BASE}/nous-contacter`,
          component: ContactUsView,
          meta: {
            pageTitleKey: `pagesTitle.${ROUTE_NAME_CONTACT_US_VIEW}`,
          },
        },
        {
          path: 'reservation-deplacement',
          name: ROUTE_NAME_BOOKING_VIEW,
          meta: {
            pageTitleKey: `pagesTitle.${ROUTE_NAME_BOOKING_VIEW}`,
          },
          component: BookingView,
          children: [
            {
              name: ROUTE_NAME_BOOKING_FORM_DATED,
              path: 'occasionnel',
              component: () => import('@/views/customer/DatedFormView.vue'),
              meta: {
                pageTitleKey: `pagesTitle.${ROUTE_NAME_BOOKING_FORM_DATED}`,
              },
              beforeEnter: (to, from, next) => {
                const { serviceRequestModification } = storeToRefs(serviceRequestModificationStore());
                // from.name est `undefined` lors d'un rechargement, par exemple
                if (from.name === undefined || (from.name === ROUTE_NAME_BOOKING_FORM_TEMPLATE && from.query.action === 'soumission' && to.name === ROUTE_NAME_BOOKING_FORM_DATED)) {
                  next();
                // Gestion du "from" pour l'utilisation du bouton Back du browser.
                // Si on ne vient pas du formulaire et qu'aucune action n'est définie dans l'objet de modification,
                // l'utilisateur est alors retourné au sélecteur de formulaire.
                } else if (from.name !== ROUTE_NAME_BOOKING_VIEW && !serviceRequestModification.value?.action) {
                  next({
                    name: ROUTE_NAME_BOOKING_VIEW,
                    replace: true,
                  });
                } else {
                  next();
                }
              },
            },
            {
              name: ROUTE_NAME_BOOKING_FORM_TEMPLATE,
              path: 'regulier',
              component: () => import('@/views/customer/TemplateFormView.vue'),
              meta: {
                pageTitleKey: `pagesTitle.${ROUTE_NAME_BOOKING_FORM_TEMPLATE}`,
              },
              beforeEnter: (to, from, next) => {
                // from.name est `undefined` lors d'un rechargement, par exemple
                if (from.name !== undefined && from.name !== ROUTE_NAME_BOOKING_VIEW) {
                  next({
                    name: ROUTE_NAME_BOOKING_VIEW,
                    replace: true,
                  });
                } else {
                  next();
                }
              },
            },
          ],
        },
        {
          name: ROUTE_NAME_CUSTOMER_VEHICLE_TRACKING,
          path: 'suivre-mon-vehicule',
          component: () => import('@/views/customer/VehicleTrackingView.vue'),
          meta: {
            displayFooter: false,
            pageTitleKey: `pagesTitle.${ROUTE_NAME_CUSTOMER_VEHICLE_TRACKING}`,
          },
        },
        {
          path: 'mes-deplacements',
          name: ROUTE_NAME_CUSTOMER_TRIPS,
          children: [
            {
              name: ROUTE_NAME_CUSTOMER_TRIP_DETAILS_DATED,
              path: 'occasionnel/:tripKey',
              component: () => import('@/views/customer/TripDetailsView.vue'),
              meta: {
                scheduleType: 'dated',
              },
            },
            {
              name: ROUTE_NAME_CUSTOMER_TRIP_DETAILS_TEMPLATE,
              path: 'regulier/:tripKey',
              component: () => import('@/views/customer/TripDetailsView.vue'),
              meta: {
                scheduleType: 'template',
              },
            },
            {
              name: ROUTE_NAME_CUSTOMER_TRIPS_LIST,
              path: '',
              redirect: `/${ROUTE_PATH_CLIENT_BASE}/mes-deplacements/occasionnel`,
              component: CustomerTripsView,
              children: [
                {
                  name: ROUTE_NAME_CUSTOMER_TRIPS_DATED,
                  path: 'occasionnel',
                  component: CustomerTripsDatedView,
                  meta: {
                    pageTitleKey: `pagesTitle.${ROUTE_NAME_CUSTOMER_TRIPS_DATED}`,
                  },
                },
                {
                  name: ROUTE_NAME_CUSTOMER_TRIPS_TEMPLATE,
                  path: 'regulier',
                  component: CustomerTripsTemplateView,
                  meta: {
                    pageTitleKey: `pagesTitle.${ROUTE_NAME_CUSTOMER_TRIPS_TEMPLATE}`,
                  },
                },
              ],
            },
          ],
        },
        {
          path: 'mon-profil',
          children: [
            {
              name: ROUTE_NAME_CUSTOMER_PROFILE,
              path: '',
              component: ProfileView,
              meta: {
                pageTitleKey: `pagesTitle.${ROUTE_NAME_CUSTOMER_PROFILE}`,
              },
            },
            {
              name: ROUTE_NAME_CUSTOMER_CHANGE_PASSWORD,
              path: 'modifier-mot-de-passe',
              component: ChangePasswordView,
              meta: {
                pageTitleKey: `pagesTitle.${ROUTE_NAME_CUSTOMER_CHANGE_PASSWORD}`,
              },
            },
            {
              name: ROUTE_NAME_CUSTOMER_MODIFY_ASSISTIVE_DEVICES,
              path: 'modifier-aides-a-la-mobilite',
              component: ModifyAssistiveDevicesView,
              meta: {
                pageTitleKey: `pagesTitle.${ROUTE_NAME_CUSTOMER_MODIFY_ASSISTIVE_DEVICES}`,
              },
            },
            {
              name: ROUTE_NAME_CUSTOMER_MODIFY_INFO,
              path: 'modifier-mes-informations-personnelles',
              component: ModifyCustomerInfoView,
              meta: {
                pageTitleKey: `pagesTitle.${ROUTE_NAME_CUSTOMER_MODIFY_INFO}`,
              },
            },
            {
              name: ROUTE_NAME_CUSTOMER_FAVOURITE_ADDRESSES,
              path: 'lieux-favoris',
              component: FavouriteAddressesView,
              meta: {
                pageTitleKey: `pagesTitle.${ROUTE_NAME_CUSTOMER_FAVOURITE_ADDRESSES}`,
              },
              beforeEnter: blockFavouriteAddressesWhenRestricted,
            },
          ],
        },
        {
          path: 'historique',
          children: [
            {
              name: ROUTE_NAME_HISTORY_VIEW,
              path: '', // History home
              component: HistoryView,
              meta: {
                pageTitleKey: `pagesTitle.${ROUTE_NAME_HISTORY_VIEW}`,
              },
            },
            {
              name: ROUTE_NAME_HISTORY_FORM_EMAIL,
              path: 'envoi-par-courriel',
              component: HistoryFormView,
              meta: {
                pageTitleKey: `pagesTitle.${ROUTE_NAME_HISTORY_FORM_EMAIL}`,
              },
            },
            {
              name: ROUTE_NAME_HISTORY_TRIP_DETAILS,
              path: ':tripKey',
              component: () => import('@/views/customer/HistoryTripDetailsView.vue'),
            },
          ],
        },
      ],
    },
    {
      path: `/${ROUTE_PATH_ACCOUNT_BASE}`,
      component: () => import('@/views/account/AccountView.vue'),
      redirect: `/${ROUTE_PATH_ACCOUNT_BASE}/connexion`,
      children: [
        {
          path: 'connexion',
          name: ROUTE_NAME_SIGN_IN,
          component: () => import('@/views/account/SignInView.vue'),
          meta: {
            nestedPage: false,
            needsAuthentication: false,
            pageTitleKey: `pagesTitle.${ROUTE_NAME_SIGN_IN}`,
          },
        },
        {
          path: 'premiere-connexion',
          name: ROUTE_NAME_FIRST_CONNECTION,
          component: () => import('@/views/account/FirstConnection/FirstConnectionView.vue'),
          meta: {
            needsAuthentication: false,
            pageTitleKey: `pagesTitle.${ROUTE_NAME_FIRST_CONNECTION}`,
          },
        },
        {
          path: 'creation-compte',
          name: ROUTE_NAME_CREATE_ACCESS,
          component: () => import('@/views/account/AccessCreationView.vue'),
          meta: {
            needsAuthentication: false,
            pageTitleKey: `pagesTitle.${ROUTE_NAME_CREATE_ACCESS}`,
          },
        },
        {
          path: 'confirmation',
          name: ROUTE_NAME_ACCOUNT_RECOVERY_EMAIL_CONFIRMATION,
          component: () => import('@/views/account/AccountRecoveryConfirmationView.vue'),
          props: (route) => ({
            type: route.query[URI_ACCOUNT_CONFIRMATION_TYPE_QUERY_PARAM_NAME],
            email: route.query[URI_ACCOUNT_CONFIRMATION_EMAIL_QUERY_PARAM_NAME],
            fileNumber: route.query[URI_ACCOUNT_CONFIRMATION_FILE_NUMBER_QUERY_PARAM_NAME],
          }),
          meta: {
            needsAuthentication: false,
            pageTitleKey: `pagesTitle.${ROUTE_NAME_ACCOUNT_RECOVERY_EMAIL_CONFIRMATION}`,
          },
        },
        {
          path: 'choisir-mot-de-passe',
          name: ROUTE_NAME_ACCOUNT_SET_PASSWORD,
          component: () => import('@/views/account/SetPasswordView.vue'),
          meta: {
            needsAuthentication: false,
            pageTitleKey: `pagesTitle.${ROUTE_NAME_ACCOUNT_SET_PASSWORD}`,
          },
        },
        {
          path: 'mot-de-passe-oublie',
          name: ROUTE_NAME_PASSWORD_LOST,
          component: () => import('@/views/account/PasswordLostView.vue'),
          meta: {
            needsAuthentication: false,
            pageTitleKey: `pagesTitle.${ROUTE_NAME_PASSWORD_LOST}`,
          },
        },
      ],
    },
    {
      path: '/nous-contacter',
      component: () => import('@/views/SignedOutContactUs.vue'),
      children: [
        {
          name: ROUTE_NAME_SIGNED_OUT_CONTACT_US,
          path: '',
          component: ContactUsView,
          meta: {
            needsAuthentication: false,
            pageTitleKey: `pagesTitle.${ROUTE_NAME_CONTACT_US_VIEW}`,
          },
        },
      ],
    },
    {
      path: '/page-non-trouvee',
      name: ROUTE_NAME_NOT_FOUND,
      component: () => import('@/views/NotFoundView.vue'),
      meta: {
        pageTitleKey: `pagesTitle.${ROUTE_NAME_NOT_FOUND}`,
      },
    },
    { path: '/:anything(.*)*', redirect: '/page-non-trouvee' },
  ],
});

router.beforeEach(validateClientCookiesEnabled);

router.afterEach((to) => {
  // affiche le panneau d'erreur des cookies désactivés uniquement si l'usager est rendu dans la page de connexion.
  if (cookiesDisabledNotificationPending && to.name === ROUTE_NAME_SIGN_IN) {
    cookiesDisabledNotificationPending = false;
    notifyCookiesDisabled();
  }
});

export default router;
